import React, { useState, useEffect } from 'react';
import Navbar from '../../components/invnavbar/invnavbar';
import Sidebar from '../../components/invsidebar/invsidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Badge } from 'primereact/badge';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import { API_URLS } from '../../Services/servicioURL';

// === INTERFACES ===
interface SalidaDetalle {
    folio: string;
    id: number;
    cve_producto: string;
    cantidad: number;
    id_sucursal_destino: number;
    cve_departamento_destino: string;
    nombre_usuario: string;
    observaciones: string | null;
    descripcion?: string;
    nombre_sucursal?: string;
    fecha?: Date;
    cve_linea?: string;
    cve_sublinea?: string;
    nombre_linea?: string;
    nombre_sublinea?: string;
}

interface Producto {
    cve: string;
    descripcion: string;
    cve_linea: string;
    cve_sublinea: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface Linea {
    cve: string;
    nombre: string; // Asumiendo que es "nombre", como en tu ejemplo de Inventario
}

interface Sublinea {
    cve: string;
    nombre: string;
    cve_linea: string;
}

const Consultor: React.FC = () => {
    const [data, setData] = useState<SalidaDetalle[]>([]);
    const [filteredData, setFilteredData] = useState<SalidaDetalle[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    // Mapas para nombres
    const [productosMap, setProductosMap] = useState<Record<string, Omit<Producto, 'cve'>>>({});
    const [sucursalesMap, setSucursalesMap] = useState<Record<number, string>>({});
    const [lineasMap, setLineasMap] = useState<Record<string, string>>({});
    const [sublineasMap, setSublineasMap] = useState<Record<string, string>>({});

    // Listas completas
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [lineas, setLineas] = useState<Linea[]>([]);
    const [sublineas, setSublineas] = useState<Sublinea[]>([]);

    // Filtros seleccionados
    const [selectedSucursal, setSelectedSucursal] = useState<number | null>(null);
    const [selectedDepartamento, setSelectedDepartamento] = useState<string | null>(null);
    const [selectedLinea, setSelectedLinea] = useState<string | null>(null);
    const [selectedSublinea, setSelectedSublinea] = useState<string | null>(null);

    // Función para crear fecha local sin problemas de zona horaria
    const createLocalDate = (dateString: string): Date => {
        if (!dateString) return new Date();
        const dateParts = dateString.split('T')[0].split('-');
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1;
            const day = parseInt(dateParts[2]);
            return new Date(year, month, day);
        }
        return new Date(dateString);
    };

    // Formatear fecha como dd/mm/yyyy
    const formatLocalDate = (date: Date | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

    // Generar etiqueta con QR
    const generateLabel = async (rowData: SalidaDetalle) => {
        try {
            const productUrl = `${window.location.origin}/QR?folio=${encodeURIComponent(rowData.folio)}`;
            const qrDataUrl = await QRCode.toDataURL(productUrl, {
                width: 300,
                margin: 1,
                color: { dark: '#000000', light: '#FFFFFF' },
                errorCorrectionLevel: 'M'
            });

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            canvas.width = 900;
            canvas.height = 600;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#cccccc';
            ctx.lineWidth = 4;
            ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

            const qrImg = new Image();
            qrImg.onload = function() {
                const qrSize = 600;
                const qrX = (canvas.width - qrSize) / 2;
                const qrY = 7;
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

                ctx.strokeStyle = '#dddddd';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(16, qrY + qrSize + 45);
                ctx.lineTo(canvas.width - 16, qrY + qrSize + 45);
                ctx.stroke();

                ctx.font = '24px Arial';
                ctx.fillStyle = '#000000';
                const producto = rowData.descripcion || 'Producto no encontrado';
                const maxWidth = canvas.width - 32;
                let line = '';
                let yPos = qrY + qrSize + 150;
                const words = producto.split(' ');
                for (let n = 0; n < words.length; n++) {
                    const testLine = line + words[n] + ' ';
                    const metrics = ctx.measureText(testLine);
                    const testWidth = metrics.width;
                    if (testWidth > maxWidth && n > 0) {
                        ctx.fillText(line, 16, yPos);
                        line = words[n] + ' ';
                        yPos += 30;
                        if (yPos > 580) break;
                    } else {
                        line = testLine;
                    }
                }
                ctx.fillText(line, 16, yPos);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `etiqueta_QR_${rowData.folio}_${rowData.cve_producto}_${new Date().getTime()}.png`;
                        link.click();
                        URL.revokeObjectURL(url);
                    }
                }, 'image/png');
            };
            qrImg.src = qrDataUrl;
        } catch (error) {
            console.error('Error al generar código QR:', error);
            generateLabelWithoutQR(rowData);
        }
    };

    const generateLabelWithoutQR = (rowData: SalidaDetalle) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = 900;
        canvas.height = 600;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 4;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

        ctx.fillStyle = '#000000';
        ctx.font = 'bold 44px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('BBT-SIS', 16, 60);

        ctx.font = 'bold 40px Arial';
        const info = [
            `Folio: ${rowData.folio}`,
            `Código: ${rowData.cve_producto}`,
            `Sucursal: ${rowData.nombre_sucursal || 'N/A'}`,
            `Depto: ${rowData.cve_departamento_destino}`,
            `Usuario: ${rowData.nombre_usuario}`,
            `Fecha: ${rowData.fecha ? formatLocalDate(rowData.fecha) : 'N/A'}`
        ];
        let yPosition = 120;
        info.forEach((line, index) => {
            ctx.fillText(line, 16, yPosition + (index * 56));
        });

        ctx.strokeStyle = '#dddddd';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(16, 460);
        ctx.lineTo(canvas.width - 16, 460);
        ctx.stroke();

        ctx.font = '28px Arial';
        const producto = rowData.descripcion || 'Producto no encontrado';
        const maxWidth = canvas.width - 32;
        const words = producto.split(' ');
        let line = '';
        let yPos = 500;
        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + ' ';
            const metrics = ctx.measureText(testLine);
            const testWidth = metrics.width;
            if (testWidth > maxWidth && n > 0) {
                ctx.fillText(line, 16, yPos);
                line = words[n] + ' ';
                yPos += 36;
                if (yPos > 580) break;
            } else {
                line = testLine;
            }
        }
        ctx.fillText(line, 16, yPos);

        canvas.toBlob((blob) => {
            if (blob) {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `etiqueta_${rowData.folio}_${new Date().getTime()}.png`;
                link.click();
                URL.revokeObjectURL(url);
            }
        }, 'image/png');
    };

    // Ordenar por folio
    const sortDataByFolio = (dataArray: SalidaDetalle[]) => {
        return [...dataArray].sort((a, b) => a.folio.localeCompare(b.folio));
    };

    // Cargar productos
    useEffect(() => {
        const fetchProductos = async () => {
            try {
                const response = await fetch(API_URLS.productos.obtener);
                if (!response.ok) throw new Error('Error al obtener productos');
                const productos: Producto[] = await response.json();

                const map = productos.reduce((acc, prod) => {
                    acc[prod.cve.trim()] = {
                        descripcion: prod.descripcion.trim(),
                        cve_linea: prod.cve_linea?.trim() || '',
                        cve_sublinea: prod.cve_sublinea?.trim() || ''
                    };
                    return acc;
                }, {} as Record<string, Omit<Producto, 'cve'>>);

                setProductosMap(map);
            } catch (error) {
                console.error("Error al cargar productos:", error);
            }
        };
        fetchProductos();
    }, []);

    // Cargar sucursales
    useEffect(() => {
        const fetchSucursales = async () => {
            try {
                const response = await fetch(API_URLS.sucursales.obtener);
                if (!response.ok) throw new Error('Error al obtener sucursales');
                const sucursales: Sucursal[] = await response.json();
                const map = sucursales.reduce((acc, s) => {
                    acc[s.id] = s.nombre.trim();
                    return acc;
                }, {} as Record<number, string>);
                setSucursalesMap(map);
                setSucursales(sucursales);
            } catch (error) {
                console.error("Error al cargar sucursales:", error);
            }
        };
        fetchSucursales();
    }, []);

    // Cargar departamentos
    useEffect(() => {
        const fetchDepartamentos = async () => {
            try {
                const response = await fetch(API_URLS.departamentos.obtener);
                if (!response.ok) throw new Error('Error al obtener departamentos');
                const departamentos: Departamento[] = await response.json();
                setDepartamentos(departamentos);
            } catch (error) {
                console.error("Error al cargar departamentos:", error);
            }
        };
        fetchDepartamentos();
    }, []);

    // Cargar líneas
    useEffect(() => {
        const fetchLineas = async () => {
            try {
                const response = await fetch(API_URLS.lineas.obtener); // Asegúrate que esta ruta exista
                if (!response.ok) throw new Error('Error al obtener líneas');
                const data: Linea[] = await response.json(); // Aquí estaba el error: falta el tipo
                const map = data.reduce((acc, l) => {
                    acc[l.cve] = l.nombre.trim();
                    return acc;
                }, {} as Record<string, string>);
                setLineas(data);
                setLineasMap(map);
            } catch (error) {
                console.error("Error al cargar líneas:", error);
            }
        };
        fetchLineas();
    }, []);

    // Cargar sublíneas
    useEffect(() => {
        const fetchSublineas = async () => {
            try {
                const response = await fetch(API_URLS.Sublineas.obtener); // Asegúrate de que sea correcta
                if (!response.ok) throw new Error('Error al obtener sublíneas');
                const data: Sublinea[] = await response.json(); // Aquí estaba el error: falta el tipo
                const map = data.reduce((acc, s) => {
                    acc[s.cve] = s.nombre.trim();
                    return acc;
                }, {} as Record<string, string>);
                setSublineas(data);
                setSublineasMap(map);
            } catch (error) {
                console.error("Error al cargar sublíneas:", error);
            }
        };
        fetchSublineas();
    }, []);

    // Cargar salida_detalle
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(API_URLS.salidas.obtener);
                if (!response.ok) throw new Error('Error al obtener los datos');
                const result = await response.json();

                const detalleList: SalidaDetalle[] = result.flatMap((item: any) =>
                    (item.salidaDetalle || []).map((detalle: any) => ({
                        ...detalle,
                        fecha: item.fecha ? createLocalDate(item.fecha) : null
                    }))
                );

                const enrichedData = sortDataByFolio(detalleList).map(item => {
                    const cveLimpia = item.cve_producto.trim();
                    const productoInfo = productosMap[cveLimpia];
                    const descripcion = productoInfo?.descripcion || 'Producto no encontrado';
                    const cve_linea = productoInfo?.cve_linea || '';
                    const cve_sublinea = productoInfo?.cve_sublinea || '';
                    const nombreSucursal = sucursalesMap[item.id_sucursal_destino];
                    const nombre_linea = lineasMap[cve_linea] || 'Línea no encontrada';
                    const nombre_sublinea = sublineasMap[cve_sublinea] || 'Sublínea no encontrada';

                    return {
                        ...item,
                        cve_producto: cveLimpia,
                        descripcion,
                        cve_linea,
                        cve_sublinea,
                        nombre_sucursal: nombreSucursal || 'Sucursal no encontrada',
                        nombre_linea,
                        nombre_sublinea
                    };
                });

                setData(enrichedData);
                setFilteredData(enrichedData);
            } catch (error) {
                console.error("Error al cargar salida_detalle:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [productosMap, sucursalesMap, lineasMap, sublineasMap]);

    // Filtrado combinado
    useEffect(() => {
        let filtered = [...data];

        if (selectedSucursal !== null) {
            filtered = filtered.filter(d => d.id_sucursal_destino === selectedSucursal);
        }
        if (selectedDepartamento !== null) {
            filtered = filtered.filter(d => d.cve_departamento_destino === selectedDepartamento);
        }
        if (selectedLinea !== null) {
            filtered = filtered.filter(d => d.cve_linea === selectedLinea);
        }
        if (selectedSublinea !== null) {
            filtered = filtered.filter(d => d.cve_sublinea === selectedSublinea);
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(d =>
                d.descripcion?.toLowerCase().includes(term) ||
                d.nombre_usuario.toLowerCase().includes(term) ||
                d.nombre_sucursal?.toLowerCase().includes(term) ||
                d.folio.toLowerCase().includes(term) ||
                d.cve_departamento_destino.toLowerCase().includes(term) ||
                d.observaciones?.toLowerCase().includes(term) ||
                d.fecha && formatLocalDate(d.fecha).includes(term) ||
                (d.nombre_linea ?? '').toLowerCase().includes(term) || // ✅ Solución al error TS 18048
                (d.nombre_sublinea ?? '').toLowerCase().includes(term) // ✅ Solución al error TS 18048
            );
        }

        setFilteredData(sortDataByFolio(filtered));
    }, [searchTerm, data, selectedSucursal, selectedDepartamento, selectedLinea, selectedSublinea]);

    // Limpiar filtros
    const clearFilters = () => {
        setSelectedSucursal(null);
        setSelectedDepartamento(null);
        setSelectedLinea(null);
        setSelectedSublinea(null);
        setSearchTerm('');
    };

    // Templates
    const dateBodyTemplate = (rowData: SalidaDetalle) => formatLocalDate(rowData.fecha);
    const cantidadBodyTemplate = (rowData: SalidaDetalle) => (
        <div style={{ textAlign: 'center' }}>
            <Badge value={rowData.cantidad} severity="info" />
        </div>
    );
    const productoBodyTemplate = (rowData: SalidaDetalle) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.2', padding: '4px 0' }}>
            {rowData.descripcion}
        </div>
    );
    const observacionesBodyTemplate = (rowData: SalidaDetalle) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.2', padding: '4px 0' }}>
            {rowData.observaciones || ''}
        </div>
    );
    const folioBodyTemplate = (rowData: SalidaDetalle) => (
        <div 
            style={{ 
                textAlign: 'center', cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline', fontWeight: 'bold',
                padding: '4px 8px', borderRadius: '4px', transition: 'all 0.2s'
            }}
            onClick={() => generateLabel(rowData)}
            title="📱 Click para generar etiqueta con código QR"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
            📱 {rowData.folio}
        </div>
    );

    // Exportar a Excel
    const exportToExcel = () => {
        const headers = ['Folio', 'Producto', 'Cantidad', 'Sucursal', 'Depto.', 'Usuario', 'Observaciones', 'Fecha', 'Línea', 'Sublínea'];
        let csvContent = [headers.join(',')];
        filteredData.forEach(d => {
            csvContent.push([
                `"${d.folio}"`,
                `"${d.descripcion || ''}"`,
                d.cantidad,
                `"${d.nombre_sucursal || ''}"`,
                `"${d.cve_departamento_destino}"`,
                `"${d.nombre_usuario}"`,
                `"${d.observaciones || ''}"`,
                `"${d.fecha ? formatLocalDate(d.fecha) : ''}"`,
                `"${d.nombre_linea || ''}"`,
                `"${d.nombre_sublinea || ''}"`
            ].join(','));
        });
        const blob = new Blob([`\uFEFF${csvContent.join('\n')}`], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `consultor_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    // Exportar a PDF
    const exportToPDF = () => {
        const doc = new jsPDF('l', 'pt', 'a4');
        const img = new Image();
        img.src = '/dist/img/bbtt2.png';
        img.onload = () => {
            doc.addImage(img, 'PNG', 40, 20, 65, 65);
            doc.setFontSize(18);
            doc.setTextColor(40);
            doc.text('Reporte de Consultor BBT-SIS', 150, 40);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Generado el: ${formatLocalDate(new Date())}`, 150, 60);
            doc.text(`Total de registros: ${filteredData.length}`, 150, 75);

            let yPosition = 90;
            if (selectedSucursal !== null) {
                const sucursalNombre = sucursalesMap[selectedSucursal] || 'Desconocida';
                doc.text(`Filtro Sucursal: ${sucursalNombre}`, 150, yPosition); yPosition += 15;
            }
            if (selectedDepartamento !== null) {
                const deptoNombre = departamentos.find(d => d.cve === selectedDepartamento)?.nombre || selectedDepartamento;
                doc.text(`Filtro Departamento: ${deptoNombre}`, 150, yPosition); yPosition += 15;
            }
            if (selectedLinea !== null) {
                const lineaNombre = lineasMap[selectedLinea] || 'Desconocida';
                doc.text(`Filtro Línea: ${lineaNombre}`, 150, yPosition); yPosition += 15;
            }
            if (selectedSublinea !== null) {
                const sublineaNombre = sublineasMap[selectedSublinea] || 'Desconocida';
                doc.text(`Filtro Sublínea: ${sublineaNombre}`, 150, yPosition); yPosition += 15;
            }

            const tableData = filteredData.map(d => [
                d.folio,
                d.descripcion || '',
                d.cantidad.toString(),
                d.nombre_sucursal || '',
                d.cve_departamento_destino,
                d.nombre_usuario,
                d.observaciones || '',
                d.fecha ? formatLocalDate(d.fecha) : ''
            ]);

            autoTable(doc, {
                head: [['Folio', 'Producto', 'Cant.', 'Sucursal', 'Depto.', 'Usuario', 'Obs.', 'Fecha']],
                body: tableData,
                startY: Math.max(yPosition, 105),
                styles: { fontSize: 7, cellPadding: 2 },
                headStyles: { fillColor: [41, 128, 185] },
                columnStyles: { 0: { halign: 'center' }, 2: { halign: 'center' }, 7: { halign: 'center' } }
            });
            doc.save(`consultor_${new Date().toISOString().slice(0, 10)}.pdf`);
        };
    };

    return (
        <div>
            <Navbar />
            <Sidebar />
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                {/* Header */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            🔍 Consultor de Salidas
                        </h1>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
                            backgroundColor: '#3b82f6', color: 'white', borderRadius: '20px',
                            fontSize: '0.85rem', fontWeight: '600'
                        }}>
                            <i className="pi pi-database"></i>
                            {loading ? 'CARGANDO...' : 'DATOS LISTOS'}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Filtros */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '1.2rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🎛️ Filtros y Búsqueda
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>🏢 Sucursal</label>
                            <Dropdown value={selectedSucursal} options={sucursales} onChange={(e) => setSelectedSucursal(e.value)} optionLabel="nombre" optionValue="id" placeholder="Todas" showClear style={{ width: '100%', height: '40px' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>🏬 Departamento</label>
                            <Dropdown value={selectedDepartamento} options={departamentos} onChange={(e) => setSelectedDepartamento(e.value)} optionLabel="nombre" optionValue="cve" placeholder="Todos" showClear style={{ width: '100%', height: '40px' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>🏷️ Línea</label>
                            <Dropdown value={selectedLinea} options={lineas} onChange={(e) => setSelectedLinea(e.value)} optionLabel="nombre" optionValue="cve" placeholder="Todas" showClear style={{ width: '100%', height: '40px' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>🗂️ Sublínea</label>
                            <Dropdown value={selectedSublinea} options={sublineas} onChange={(e) => setSelectedSublinea(e.value)} optionLabel="nombre" optionValue="cve" placeholder="Todas" showClear style={{ width: '100%', height: '40px' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>🔍 Búsqueda Global</label>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.9rem' }}></i>
                                <InputText placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: '100%', height: '40px', paddingLeft: '40px', border: '2px solid #e5e7eb', borderRadius: '8px' }} />
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Button icon="pi pi-filter-slash" label="Limpiar Filtros" size="small" severity="secondary" outlined onClick={clearFilters} style={{ height: '36px', borderRadius: '8px' }} />
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                                backgroundColor: '#f3f4f6', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', color: '#374151'
                            }}>
                                📊 <span style={{ color: '#3b82f6' }}>{filteredData.length}</span> de <span style={{ color: '#10b981' }}>{data.length}</span> registros
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button icon="pi pi-file-excel" label="Excel" size="small" severity="success" onClick={exportToExcel} disabled={filteredData.length === 0} style={{ height: '36px', borderRadius: '8px', fontWeight: '600' }} />
                            <Button icon="pi pi-file-pdf" label="PDF" size="small" severity="danger" onClick={exportToPDF} disabled={filteredData.length === 0} style={{ height: '36px', borderRadius: '8px', fontWeight: '600' }} />
                        </div>
                    </div>
                </div>

                {/* Tabla */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}>
                    <DataTable value={filteredData} responsiveLayout="scroll" stripedRows scrollable scrollHeight="600px" emptyMessage={loading ? "🔄 Cargando..." : "📭 No hay registros"} paginator rows={50}>
                        <Column field="folio" header="📱 Folio" sortable body={folioBodyTemplate} style={{ width: '80px', textAlign: 'center' }} />
                        <Column field="descripcion" header="📦 Producto" sortable body={productoBodyTemplate} style={{ width: '200px', verticalAlign: 'top' }} />
                        <Column field="cantidad" header="📊 Cant." sortable body={cantidadBodyTemplate} style={{ width: '70px', textAlign: 'center' }} />
                        <Column field="nombre_sucursal" header="🏢 Sucursal" sortable style={{ width: '120px', textAlign: 'center' }} />
                        <Column field="cve_departamento_destino" header="🏬 Depto." sortable style={{ width: '80px', textAlign: 'center' }} />
                        <Column field="nombre_usuario" header="👤 Usuario" sortable style={{ width: '120px', textAlign: 'center' }} />
                        <Column field="observaciones" header="📝 Obs." sortable body={observacionesBodyTemplate} style={{ width: '160px', verticalAlign: 'top' }} />
                        <Column field="fecha" header="📅 Fecha" sortable body={dateBodyTemplate} style={{ width: '100px', textAlign: 'center' }} />
                        </DataTable>
                </div>
            </div>
        </div>
    );
};

export default Consultor;